import React, { useEffect, useState } from 'react'
import { AthleteRoutineDataList, AthleteRoutineProps } from './components/AthleteRoutineDataList'
import { useLocation, useParams } from 'react-router-dom'
import { SubHeader } from '../../../components/SubHeader'
import api from '../../../services/api'
import { Button } from '../../../components/Button'
import { useTranslation } from 'react-i18next'
import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { addBreadcrumb } from '../../../redux/reducers/breadcrumb/actions'
import { Stack } from '@mui/material'
import { Helmet } from 'react-helmet-async'
import { AthleteRoutineForm } from './components/AthleteRoutineForm'
import { InputSearch } from '../../../components/InputSearch'
import { AssignRoutineToModal } from '../AssignRoutineToModal'
import { TreatEmpty } from 'components/TreatEmpty'
import { LoadingPage } from 'components/LoadingPage'
import { showMessage } from 'redux/reducers/snackbar/snackbar.actions'
import { CopyRoutineToModal } from '../CopyRoutineToModal'
import { Add, ContentCopy } from '@mui/icons-material'
import { RootState } from '../../../redux/rootReducer'
import Iconify from '../../../components/Iconify'
import { TreatEmptySuggestion } from 'components/TreatEmptySuggestion'

export const AthleteRoutineList = () => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const { t } = useTranslation()
  const { professionalAthleteId } = useParams()
  const [data, setData] = useState<AthleteRoutineProps[]>([])
  const [dataSearch, setDataSearch] = useState<AthleteRoutineProps[]>([])
  const [modalVisible, setModalVisible] = useState(false)
  const [modalCopyVisible, setModalCopyVisible] = useState(false)
  const [athleteRoutine, setAthleteRoutine] = useState<any>()
  const [isRoutinesVisible, setIsRoutinesVisible] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const { professionalAthlete } = useAppSelector((state: RootState) => state.summaryAthlete)
  const [showInstruction, setShowInstruction] = useState<boolean>(false)

  useEffect(() => {
    const body = document.querySelector('body')
    if (body) {
      body.style.overflow = modalVisible ? 'hidden' : 'auto'
    }
    dataFetch().catch(console.error)
  }, [modalVisible])

  useEffect(() => {
    if (location?.state?.reloadRoutines) {
      dataFetch().catch(console.error)
      location.state.reloadRoutines = false
    }
  }, [location?.state?.reloadRoutines])

  const dataFetch = async () => {
    if (professionalAthlete) {
      const { name, surname } = professionalAthlete.athlete
      dispatch(addBreadcrumb({ label: `${name} ${surname}`, path: location.pathname }))
    }
    try {
      setLoading(true)
      const { data } = await api.get(`/professional-athlete-routines/${professionalAthleteId}`)
      setData(data)
      setDataSearch(data)
      setShowInstruction(data.length > 0 ? false : true)
    } catch {
      dispatch(showMessage({ message: `${t('general.error')}`, type: 'error' }))
    } finally {
      setLoading(false)
    }
  }

  const handleLoadRoutinesAfterCreate = (value: boolean) => {
    if (value) {
      dataFetch().catch(console.error)
    }
  }

  const handleOpenModal = () => {
    setAthleteRoutine(null)
    setModalVisible(!modalVisible)
  }

  const handleSearchField = (searchReturn: any) => {
    setDataSearch(searchReturn)
  }

  const handleEdit = (athleteRoutine: any) => {
    setAthleteRoutine(athleteRoutine)
    setModalVisible(true)
  }

  const handleCopy = (athleteRoutine: any) => {
    setAthleteRoutine(athleteRoutine)
    setModalCopyVisible(true)
  }

  const handleCheckEmpty = (athleteList: AthleteRoutineProps[]) => {
    setData(athleteList)
    setDataSearch(athleteList)
    if (athleteList.length === 0) {
      setShowInstruction(true)
    }
  }

  return (
    <>
      <Helmet>
        <title> Athlete Routine | DWF </title>
      </Helmet>
      <Stack>
        <SubHeader path={location.pathname} goBack={false} />
        <AssignRoutineToModal
          isVisible={isRoutinesVisible}
          professionalAthleteToAssign={professionalAthleteId}
          onLoadRoutinesAfterCreate={(value) => handleLoadRoutinesAfterCreate(value)}
          loading={loading}
          onClose={() => setIsRoutinesVisible(false)}
        />
        <CopyRoutineToModal
          isVisible={modalCopyVisible}
          athleteRoutine={athleteRoutine}
          professionalAthleteId={professionalAthleteId}
          onClose={() => setModalCopyVisible(false)}
        />
        <AthleteRoutineForm
          isVisible={modalVisible}
          athleteRoutine={athleteRoutine}
          professionalAthleteId={professionalAthleteId}
          onSubmitted={async () => {
            setModalVisible(false)
            await dataFetch()
          }}
          onClose={() => setModalVisible(false)}
          loading={loading}/>
        <Stack
          mt={{ xs: 1, sm: 3, md: 3 }}
          mb={{ xs: 2, sm: 0, md: 0 }}
          spacing={1}
          justifyContent="space-between"
          direction={{ xs: 'column', sm: 'row' }}
        >
          <Stack flex={1}>
            <InputSearch
              title={`${t('search.titleRoutine')}`}
              data={data}
              params={['labels', 'name', 'note']}
              action={handleSearchField} />
          </Stack>
          <Stack
            direction={{ xs: 'column', sm: 'row', md: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center', md: 'center' }}
            spacing={2}
          >
            <Stack>
              <Button
                title={`${t('routine.importRoutine')}`}
                startIcon={<Iconify icon={'fe:import'} />}
                onClick={() => { setIsRoutinesVisible(true) }}
              />
            </Stack>
            <Stack>
              <Button
                title={`${t('routine.newRoutine')}`}
                startIcon={<Add />}
                onClick={handleOpenModal}
              />
            </Stack>
          </Stack>
        </Stack>
        { loading
          ? <LoadingPage />
          : <>
            { dataSearch.length === 0 && data.length > 0
              ? <TreatEmpty subTitleMessage={t('general.searchEmpty')} />
              : data.length === 0
                ? <TreatEmptySuggestion
                  textSuggestion={[
                    t('routine.title.suggestion_1'),
                    t('routine.title.suggestion_2', { newRoutine: t('routine.newRoutine') }),
                    t('routine.title.suggestion_3'),
                    t('routine.title.suggestion_4', { importRoutine: t('routine.importRoutine') })
                  ]}
                  showInstruction={showInstruction}
                />
                : <AthleteRoutineDataList
                  items={dataSearch}
                  onEdit={handleEdit}
                  onAthleteList={handleCheckEmpty}
                  onHandleCopy={handleCopy}
                />
            }
          </>
        }
      </Stack>
    </>
  )
}
